'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { captureException } from '@sentry/nextjs';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import React, { useMemo } from 'react';
import { Button } from '@/features/button';
import { TextField } from '@/features/form';
import { EXTRA_BOTTOM } from '@/features/toast-container/constant';
import { toEnglish } from '@/utils/string-helpers';
import { ContactUsFormValues } from '../types';
import { generateErrorMessage } from '../utils/generate-error-message';
import { useContactUs } from '../utils/use-contact-us';
import { ContactUsFormValidation } from '../validation';
function ContactUsForm() {
  const {
    mutateAsync: postContactUsData,
    isPending,
    isError,
    error
  } = useContactUs();
  const {
    register,
    getValues,
    handleSubmit,
    formState: {
      errors
    }
  } = useForm<ContactUsFormValues>({
    reValidateMode: 'onChange',
    resolver: zodResolver(ContactUsFormValidation),
    defaultValues: {
      fullName: '',
      phone: '',
      emailAddress: '',
      message: ''
    }
  });
  const submitHandler = async () => {
    const formData = getValues();
    try {
      const contactUsFormData = new FormData();
      contactUsFormData.append('Name', formData.fullName || '');
      contactUsFormData.append('MobileNumber', toEnglish(formData?.phone || ''));
      contactUsFormData.append('Email', formData?.emailAddress || '');
      contactUsFormData.append('Message', formData?.message || '');
      await postContactUsData({
        body: contactUsFormData
      });
      if (!isError) {
        toast.success('پیام شما با موفقیت ارسال شد.', {
          duration: 3000,
          id: 'notify-me-toast',
          style: {
            maxWidth: 'max-content',
            bottom: EXTRA_BOTTOM
          }
        });
      }
    } catch (error) {
      captureException(error);
    }
  };
  const apiFieldsErrors = useMemo(() => generateErrorMessage(error?.at(0)), [error]);
  return <div className='col-span-full mb-8 flex flex-col gap-2 rounded border border-border-gray-light bg-surface-solid-0 px-2 py-3 text-text-black shadow-sm md:mb-0 md:gap-6 md:px-4 md:py-6' data-sentry-component="ContactUsForm" data-sentry-source-file="contact-us-form.tsx">
            <span className='text-center text-body-14 font-bold md:text-body-16'>
                روزهای شنبه تا پنجشنبه، از ساعت 9 الی 21 پاسخگوی سوالات شما هستیم.
            </span>
            <span className='text-right text-body-14 md:text-body-16'>
                در صورتی که خارج از ساعات اداری تماس گرفتید، می‌توانید از طریق فرم زیر پیام خود را بگذارید. کارشناسان ما
                پس از بررسی با شما تماس خواهند گرفت.
            </span>

            <form onSubmit={handleSubmit(submitHandler)} className='flex flex-col items-end justify-between gap-2 md:gap-6'>
                <div className='flex w-full flex-col gap-2 md:flex-row md:gap-6 [&>div]:w-full'>
                    <TextField inputSize='large' title='نام و نام‌ خانوادگی*' id='fullName' registeredField={register('fullName')} inlineMessage={{
          message: errors?.fullName?.message || apiFieldsErrors?.fullName || '',
          type: 'error',
          showIcon: true
        }} data-sentry-element="TextField" data-sentry-source-file="contact-us-form.tsx" />
                    <TextField inputSize='large' title='شماره تماس*' id='phone' registeredField={register('phone')} direction='ltr' inlineMessage={{
          message: errors?.phone?.message || apiFieldsErrors?.phone || '',
          type: 'error',
          showIcon: true
        }} maxLength={11} data-sentry-element="TextField" data-sentry-source-file="contact-us-form.tsx" />

                    <TextField inputSize='large' title='ایمیل' id='emailAddress' required={false} registeredField={register('emailAddress')} direction='ltr' inlineMessage={{
          message: errors?.emailAddress?.message || apiFieldsErrors?.emailAddress || '',
          type: 'error',
          showIcon: true
        }} data-sentry-element="TextField" data-sentry-source-file="contact-us-form.tsx" />
                </div>

                <div className='w-full'>
                    <TextField inputSize='large' isTextArea id='review' title='متن پیام*' registeredField={register('message')} autoFocus={false} inlineMessage={{
          message: errors?.message?.message || apiFieldsErrors?.message || '',
          type: 'error',
          showIcon: true
        }} data-sentry-element="TextField" data-sentry-source-file="contact-us-form.tsx" />
                </div>

                <Button graphicStyle='filled' size='large' isLoading={isPending} className='w-full md:mr-auto md:w-39' disabled={isPending} data-sentry-element="Button" data-sentry-source-file="contact-us-form.tsx">
                    ارسال
                </Button>
            </form>
        </div>;
}
export default ContactUsForm;